import { useEffect, useRef, useState } from 'react';
import type { MouseEvent } from 'react';
import HandPanel from './HandPanel';
import type { HandPanelProps } from './HandPanel';
import { HandCardState } from '@/types/game';

interface HandRailProps {
  cards: HandCardState[];
  onCollapseChange?: (collapsed: boolean) => void;
  onCardMouseDown?: (cardId: string, event: MouseEvent<HTMLDivElement>) => void;
  onBoundsChange?: (rect: DOMRect | null) => void;
  onCardMouseEnter?: (cardId: string) => void;
  onCardMouseLeave?: (cardId: string) => void;
  isHighlighted?: boolean;
  isDraggingFromHand?: boolean;
  viewerId?: string | null;
  playerNames?: Record<string, string>;
  title?: string;
  onFlipCard?: (cardId: string) => void;
  onSelectCard?: (cardId: string) => void;
  selectedCardId?: string | null;
  selectionColor?: string | null;
}

export default function HandRail({
  cards,
  onCollapseChange,
  onCardMouseDown,
  onBoundsChange,
  onCardMouseEnter,
  onCardMouseLeave,
  isHighlighted = false,
  isDraggingFromHand = false,
  viewerId,
  playerNames,
  title = 'Your Hand',
  onFlipCard,
  onSelectCard,
  selectedCardId,
  selectionColor
}: HandRailProps) {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  useEffect(() => {
    if (!onBoundsChange) return;
    const updateBounds = () => {
      if (containerRef.current) {
        onBoundsChange(containerRef.current.getBoundingClientRect());
      } else {
        onBoundsChange(null);
      }
    };
    updateBounds();

    if (containerRef.current && typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => updateBounds());
      observer.observe(containerRef.current);
      resizeObserverRef.current = observer;
    }

    window.addEventListener('scroll', updateBounds, true);
    window.addEventListener('resize', updateBounds);

    return () => {
      resizeObserverRef.current?.disconnect();
      window.removeEventListener('scroll', updateBounds, true);
      window.removeEventListener('resize', updateBounds);
      onBoundsChange?.(null);
    };
  }, [onBoundsChange]);

  useEffect(() => {
    if (!onBoundsChange) return;
    requestAnimationFrame(() => {
      if (containerRef.current) {
        onBoundsChange(containerRef.current.getBoundingClientRect());
      }
    });
  }, [isCollapsed, cards.length, onBoundsChange]);

  const handPanelProps: HandPanelProps = {
    cards,
    viewerId,
    playerNames,
    title,
    isHighlighted,
    collapsed: isCollapsed,
    onCollapseChange: collapsedValue => {
      setIsCollapsed(collapsedValue);
      onCollapseChange?.(collapsedValue);
    },
    onCardMouseDown,
    onCardMouseEnter,
    onCardMouseLeave,
    onFlipCard,
    onSelectCard,
    selectedCardId,
    allowDrag: true,
    allowFlip: true,
    allowSelect: true,
    cardWidthStrategy: 'adaptive',
    selectionColor
  };

  const collapsedTranslateClass = 'translate-y-[calc(100%-46px-0.5rem)] sm:translate-y-[calc(100%-46px-0.75rem)]';

  return (
    <div
      ref={containerRef}
      className={`fixed bottom-1 left-0 right-0 z-40 px-2 pb-1 transition-transform duration-300 sm:bottom-2 sm:px-4 sm:pb-2 ${
        isCollapsed ? collapsedTranslateClass : 'translate-y-0'
      } ${isDraggingFromHand ? 'pointer-events-none' : 'pointer-events-auto'}`}
    >
      <div
        className="mx-auto w-full max-w-[94vw] md:w-fit md:max-w-[56vw]"
        style={{ pointerEvents: isDraggingFromHand ? 'none' : 'auto' }}
      >
        <HandPanel {...handPanelProps} />
      </div>
    </div>
  );
}
